import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { PageHeader, Card, Badge, Modal, Btn, Field, inputCls } from "../components/common";
import { toast } from "sonner";
import { Plus, BadgePercent } from "lucide-react";
import { fmtIDR, fmtDate } from "../lib/format";

const EMPTY = { code: "", title: "", description: "", discount_type: "nominal", discount_value: "", quota_total: "", quota_per_customer: "", valid_from: "", valid_until: "", terms: "", unit_types: "", payment_schemes: [] };

export default function PromoVoucher() {
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/marketing/promos").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/marketing/promo-report").then((r) => setReport(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = () => {
    if (!form.code.trim() || !form.title.trim()) return toast.error("Kode & judul wajib diisi");
    api.post("/marketing/promos", {
      ...form,
      discount_value: parseFloat(form.discount_value) || 0,
      quota_total: parseInt(form.quota_total) || 0,
      quota_per_customer: parseInt(form.quota_per_customer) || 0,
      valid_from: form.valid_from || null, valid_until: form.valid_until || null,
      unit_types: form.unit_types ? form.unit_types.split(",").map((s) => s.trim()).filter(Boolean) : [],
    }).then(() => { toast.success("Promo dibuat"); setAddOpen(false); setForm(EMPTY); load(); })
      .catch((e) => toast.error(errMsg(e)));
  };
  const toggle = (p) => api.put(`/marketing/promos/${p.id}`, { active: !p.active })
    .then(() => { toast.success("Status diubah"); load(); }).catch((e) => toast.error(errMsg(e)));

  const rep = Object.fromEntries(report.map((r) => [r.code, r]));
  return (
    <div data-testid="promo-page">
      <PageHeader title="Promo & Voucher" subtitle="Voucher yang dapat dipakai konsumen di aplikasi ABAD JAYA LIVING saat booking" testid="promo-header">
        <Btn onClick={() => setAddOpen(true)} data-testid="promo-add-btn"><Plus className="w-4 h-4" /> Buat Promo</Btn>
      </PageHeader>

      <div className="space-y-2" data-testid="promo-list">
        {rows.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada promo.</p></Card>}
        {rows.map((p) => {
          const rp = rep[p.code] || {};
          return (
            <Card key={p.id} className="!p-4" testid={`promo-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <BadgePercent className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-mono font-medium text-slate-200">{p.code}</span>
                <span className="text-slate-300 text-sm">{p.title}</span>
                <Badge tone={p.active ? "emerald" : "slate"}>{p.active ? "Aktif" : "Nonaktif"}</Badge>
                <Badge tone="cyan">{p.discount_type === "persen" ? `${p.discount_value}%` : fmtIDR(p.discount_value)}</Badge>
                <button onClick={() => toggle(p)} className="ml-auto text-xs text-amber-300 hover:underline" data-testid={`promo-toggle-${p.id}`}>
                  {p.active ? "Nonaktifkan" : "Aktifkan"}
                </button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {p.valid_from || "…"} s/d {p.valid_until || "∞"} · kuota {p.used_count}/{p.quota_total || "∞"} · per konsumen {p.quota_per_customer || "∞"}
                {p.unit_types?.length > 0 && ` · tipe: ${p.unit_types.join(", ")}`}
              </div>
              {p.terms && <div className="text-[11px] text-slate-500 mt-0.5">S&K: {p.terms}</div>}
              <div className="flex gap-1.5 mt-2 flex-wrap">
                <Badge tone="amber">Lead pakai: {rp.leads ?? 0}</Badge>
                {(rp.per_sales || []).map((s) => <Badge key={s.sales} tone="cyan">{s.sales}: {s.jumlah}</Badge>)}
              </div>
            </Card>
          );
        })}
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Buat Promo / Voucher" testid="promo-modal">
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kode voucher"><input className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MERDEKA26" data-testid="promo-code-input" /></Field>
            <Field label="Judul"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Promo Merdeka" data-testid="promo-title-input" /></Field>
          </div>
          <Field label="Deskripsi"><textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="promo-desc-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipe diskon">
              <select className={inputCls} value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="promo-type-select">
                <option value="nominal">Nominal (Rp)</option><option value="persen">Persen (%)</option>
              </select>
            </Field>
            <Field label="Nilai diskon"><input type="number" className={inputCls} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="promo-value-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kuota total (0=tak terbatas)"><input type="number" className={inputCls} value={form.quota_total} onChange={(e) => setForm({ ...form, quota_total: e.target.value })} data-testid="promo-quota-input" /></Field>
            <Field label="Kuota per konsumen"><input type="number" className={inputCls} value={form.quota_per_customer} onChange={(e) => setForm({ ...form, quota_per_customer: e.target.value })} data-testid="promo-quota-cust-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Berlaku dari"><input type="date" className={inputCls} value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} data-testid="promo-from" /></Field>
            <Field label="Berlaku sampai"><input type="date" className={inputCls} value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="promo-until" /></Field>
          </div>
          <Field label="Tipe unit (koma, opsional)"><input className={inputCls} value={form.unit_types} onChange={(e) => setForm({ ...form, unit_types: e.target.value })} placeholder="36/72, 45/90" data-testid="promo-types-input" /></Field>
          <Field label="Syarat & ketentuan"><textarea className={inputCls} rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="promo-terms-input" /></Field>
          <Btn onClick={create} data-testid="promo-create-btn">Simpan Promo</Btn>
        </div>
      </Modal>
    </div>
  );
}
